import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import usePageTitle from "../../hooks/usePageTitle";
import AppShell from "../AppShell";
import ImageWithFallback from "../ImageWithFallback";
import {
  browsablePokemonSets,
  getPokemonSetImageCandidates,
  getPokemonSetTheme,
} from "../../utils/pokemonSets";

const panelSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#ffffff",
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
};

const interactiveCardSx = {
  borderRadius: 2.25,
  border: "1px solid",
  borderColor: "rgba(12, 35, 64, 0.08)",
  backgroundColor: "#f9fbfd",
  transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease",
  cursor: "pointer",
  textDecoration: "none",
  color: "inherit",
  display: "block",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 16px 30px rgba(15, 23, 42, 0.12)",
    borderColor: "rgba(18, 89, 156, 0.20)",
    backgroundColor: "#ffffff",
  },
};

function PlaceholderImage({ label }) {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "text.secondary",
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      {label}
    </Box>
  );
}

function sortSets(sets, sortBy) {
  const ordered = [...sets];
  if (sortBy === "name_asc") {
    return ordered.sort((a, b) => a.set_name.localeCompare(b.set_name));
  }
  if (sortBy === "name_desc") {
    return ordered.sort((a, b) => b.set_name.localeCompare(a.set_name));
  }
  if (sortBy === "year_desc") {
    return ordered.sort((a, b) => (b.year || 0) - (a.year || 0) || a.displayOrder - b.displayOrder);
  }
  if (sortBy === "year_asc") {
    return ordered.sort((a, b) => (a.year || 0) - (b.year || 0) || a.displayOrder - b.displayOrder);
  }
  return ordered.sort((a, b) => a.displayOrder - b.displayOrder);
}

export default function PokemonSetsPage() {
  usePageTitle("Pokemon Sets");

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("metadata");

  const filteredSets = useMemo(() => {
    const query = String(searchTerm || "").trim().toLowerCase();
    const base = !query
      ? browsablePokemonSets
      : browsablePokemonSets.filter((set) => {
          const codeText = [set.code, ...(set.aliasCodes || [])].join(" ").toLowerCase();
          const aliasText = [...(set.aliases || []), ...(set.matchTerms || [])].join(" ").toLowerCase();
          return (
            set.set_name.toLowerCase().includes(query) ||
            codeText.includes(query) ||
            aliasText.includes(query) ||
            String(set.year || "").includes(query)
          );
        });

    return sortSets(base, sortBy);
  }, [searchTerm, sortBy]);

  return (
    <AppShell>
      <Box sx={{ px: { xs: 0, md: 1 }, py: { xs: 1, md: 1.5 } }}>
        <Stack spacing={{ xs: 2.5, md: 3 }}>
          <Paper
            elevation={0}
            sx={{
              ...panelSx,
              p: { xs: 2.5, md: 3.25 },
              background: "linear-gradient(135deg, #18304f 0%, #255a8a 58%, #f2c55b 100%)",
              borderColor: "rgba(255,255,255,0.10)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(circle at top right, rgba(255,255,255,0.16), transparent 30%), radial-gradient(circle at bottom left, rgba(255,255,255,0.10), transparent 26%)",
                pointerEvents: "none",
              }}
            />
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  color: "rgba(255,255,255,0.72)",
                  fontWeight: 800,
                  letterSpacing: 1.15,
                  mb: 1,
                }}
              >
                Metadata-Driven Browse
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  color: "#ffffff",
                  fontWeight: 900,
                  lineHeight: 1.08,
                  mb: 1.1,
                  maxWidth: 760,
                  fontSize: { xs: "2rem", md: "2.7rem" },
                }}
              >
                Pokemon Sets
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.82)",
                  maxWidth: 860,
                  lineHeight: 1.65,
                  fontSize: { xs: "0.98rem", md: "1.02rem" },
                }}
              >
                Browse every Pokemon set in metadata order, search by name, code, or year, and jump into dedicated set pages that are ready to support database-backed inventory as mappings go live.
              </Typography>
            </Box>
          </Paper>

          <Paper elevation={0} sx={panelSx}>
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: 2.25,
              }}
            >
              <TextField
                label="Search Sets"
                placeholder="Search by set name, code, or year"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                fullWidth
              />
              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 220 } }}>
                <InputLabel id="pokemon-set-sort-label">Sort Sets</InputLabel>
                <Select
                  labelId="pokemon-set-sort-label"
                  value={sortBy}
                  label="Sort Sets"
                  onChange={(event) => setSortBy(event.target.value)}
                >
                  <MenuItem value="metadata">Metadata Order</MenuItem>
                  <MenuItem value="name_asc">Name A-Z</MenuItem>
                  <MenuItem value="name_desc">Name Z-A</MenuItem>
                  <MenuItem value="year_desc">Newest First</MenuItem>
                  <MenuItem value="year_asc">Oldest First</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 700, mb: 2 }}>
              {filteredSets.length} {filteredSets.length === 1 ? "Set" : "Sets"}
            </Typography>

            <Grid container spacing={{ xs: 1.5, md: 2 }}>
              {filteredSets.map((set) => {
                const theme = getPokemonSetTheme(set);
                const imageCandidates = getPokemonSetImageCandidates(set);

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={set.slug}>
                    <Box component={RouterLink} to={`/pokemon/sets/${set.slug}`} sx={interactiveCardSx}>
                      <Box
                        sx={{
                          height: 220,
                          backgroundColor: "#eaf0f6",
                          overflow: "hidden",
                          borderBottom: "1px solid rgba(12, 35, 64, 0.08)",
                        }}
                      >
                        {imageCandidates.length ? (
                          <ImageWithFallback
                            src={imageCandidates[0]}
                            fallbackSrcs={imageCandidates.slice(1)}
                            alt={set.set_name}
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                          />
                        ) : (
                          <PlaceholderImage label={set.code} />
                        )}
                      </Box>
                      <Box sx={{ p: 1.6 }}>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.05 }}>
                          <Chip label={set.code} size="small" sx={{ fontWeight: 700, bgcolor: theme.chipBg, color: "#10233f" }} />
                          <Chip label={`${set.year}`} size="small" sx={{ fontWeight: 700 }} />
                          {set.promo ? <Chip label="Promo" size="small" color="warning" sx={{ fontWeight: 700 }} /> : null}
                        </Stack>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900, color: "#10233f", mb: 0.4, lineHeight: 1.3 }}>
                          {set.set_name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5 }}>
                          {set.total ? `${set.total} cards` : "Card total varies"}
                        </Typography>
                        {set.notes ? (
                          <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55 }}>
                            {set.notes}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Stack>
      </Box>
    </AppShell>
  );
}
